import React, { useState } from "react";
import axios from "axios";
import { X, Bell, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function GlobalAlertModal({ 
  isOpen, 
  onClose, 
  country,
  marketType,
  marketName,
  category,
  currentPrice,
  currency,
  unit,
  token
}) {
  const [condition, setCondition] = useState("above");
  const [targetPrice, setTargetPrice] = useState(currentPrice || 0);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Please sign in to create alerts");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/user/global-alerts`,
        {
          country,
          market_type: marketType,
          market_name: marketName,
          category,
          condition,
          target_price: parseFloat(targetPrice),
          currency,
          is_active: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Alert created for ${marketName}`);
      onClose();
    } catch (err) {
      toast.error("Failed to create alert");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (value) => {
    if (currency === "BRL") return `R$${value}`;
    if (currency === "EUR") return `€${value}`;
    if (currency === "GBP") return `${value}p`;
    if (currency === "USD") return `$${value}`;
    if (currency === "INR") return `₹${value}`;
    if (currency === "MXN") return `$${value} MXN`;
    if (currency === "CNY") return `¥${value}`;
    return value;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#111827] border border-[#1F2937] w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1F2937]">
          <div className="flex items-center gap-3">
            <Bell size={24} className="text-[#F59E0B]" />
            <div>
              <h2 className="text-lg font-serif text-white">Create Price Alert</h2>
              <p className="text-xs text-[#8B8580]">{marketName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1F2937] transition-colors">
            <X size={20} className="text-[#8B8580]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Current Price */}
          <div className="bg-[#1F2937] p-3">
            <span className="text-xs text-[#6B6661] block mb-1">Current Price</span>
            <span className="font-mono text-xl text-white">{formatCurrency(currentPrice)} <span className="text-sm text-[#6B6661]">{unit}</span></span>
          </div>

          {/* Condition */}
          <div>
            <label className="text-xs text-[#6B6661] block mb-2">Alert me when price goes</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCondition("above")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 transition-colors ${
                  condition === "above" 
                    ? "bg-[#059669] text-white" 
                    : "bg-[#1F2937] text-[#8B8580] hover:text-white"
                }`}
              >
                <CaretUp size={16} weight="bold" />
                Above
              </button>
              <button
                type="button"
                onClick={() => setCondition("below")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 transition-colors ${
                  condition === "below" 
                    ? "bg-[#DC2626] text-white" 
                    : "bg-[#1F2937] text-[#8B8580] hover:text-white"
                }`}
              >
                <CaretDown size={16} weight="bold" />
                Below
              </button>
            </div>
          </div>

          {/* Target Price */}
          <div>
            <label className="text-xs text-[#6B6661] block mb-2">Target Price ({currency})</label>
            <input
              type="number"
              step="0.01"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              className="w-full px-4 py-3 bg-[#1F2937] border border-[#374151] text-white font-mono text-lg focus:outline-none focus:border-[#0F766E]"
              required
            />
          </div>

          {/* Category (if applicable) */}
          {category && (
            <div className="bg-[#1F2937] p-3">
              <span className="text-xs text-[#6B6661] block mb-1">Category</span>
              <span className="text-sm text-white">{category.replace(/_/g, " ")}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#F59E0B] text-[#33302E] font-sans font-medium hover:bg-[#D97706] transition-colors disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Alert"}
          </button>

          <p className="text-xs text-[#6B6661] text-center">
            You'll be notified when the price {condition === "above" ? "rises above" : "falls below"} {formatCurrency(targetPrice)}
          </p>
        </form>
      </div>
    </div>
  );
}
